import React, { useState, useRef, useEffect } from "react";
import { useTheme } from "react-jss";
import classnames from "classnames";
import { Dictionary } from "lodash";
import { RendererProps } from "../BaseRenderer";
import { ChoiceModel } from "../../../models/ChoiceModel";
import { QuestionType } from "../../../models/enums/QuestionType";
import { MentionModel } from "../../../models/MentionModel";
import { VariableModel } from "../../../models/VariableModel";
import useQuestion from "../../../hooks/useQuestion";
import useTranslatable from "../../../hooks/useTranslatable";
import { FontAwesomeIcon } from "../../shared/FontAwesome";
import { removeHtmlTags } from "../../../utils";
import { translate } from "../../../models/utilities/translationUtilities";
import useStyles from "./ChoiceListTextHighlighterDropdownRenderer.styles";
import { MOBILE_WIDTH } from "../../../consts";

interface Props extends RendererProps {
    variable: VariableModel;
    openEndValue?: { [key: string]: string };
    onClick?(choice: ChoiceModel): void;
    onOpenEndChange?(choice: ChoiceModel, text: string): void;
    onOpenEndBlur?(choice: ChoiceModel): void;
    mentions: { [variableName: string]: MentionModel[] };
    choices: ChoiceModel[];
    gridRendering?: boolean;
    onReset(): void;
    language: string;
    maxAnswers?: number;
    isSelected?: boolean;
    highlightedWordsByCategories: Dictionary<any[]>;
    selectedChoiceModels: ChoiceModel[];
    highlightedWords: Dictionary<any[]>;
    isChoiceAlreadyHighlighted: (choice: ChoiceModel, variableName: string) => boolean;
    duplicateWords: string[];
    toggleIsCategorySelected(isSelected: boolean, enable: boolean): void;
    onDeleteHighlight(shouldSave: boolean): void;
    onSaveHighlight?(): void;
    onHideChoiceListRenderer?(): void;
    hasError?: boolean;
    selectedCategoryName?: string | null;
}

interface ChoiceDropdownProps extends Props {
    isOpen: boolean;
    onClose: () => void;
    onChoiceSelect: (choice: ChoiceModel) => void;
    selectedChoice?: ChoiceModel;
    dropdownPosition: 'top' | 'bottom';
    question: any;
}

function ChoiceDropdown(props: ChoiceDropdownProps) {
    const {
        isOpen,
        onClose,
        onChoiceSelect,
        choices,
        selectedChoice,
        theme,
        dropdownPosition
    } = props;
    const classes = useStyles({ theme });
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, onClose]);

    // Handle escape key
    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen, onClose]);

    // Early return AFTER all hooks have been called
    if (!isOpen) return null;

    const handleChoiceClick = (choice: ChoiceModel) => {
        onChoiceSelect(choice);
        // Don't close the dropdown here - let the parent component handle it
    };

    return (
        <div
            className={classnames(
                classes.dropdownList,
                dropdownPosition === 'top' ? classes.dropdownListTop : classes.dropdownListBottom
            )}
            ref={dropdownRef}
        >
            {choices.map((choice, index) => {
                const isSelected = selectedChoice?.name === choice.name;
                const choiceText = removeHtmlTags(translate(choice, "TEXT", props.language));
                return (
                    <div
                        key={choice.name}
                        className={classnames(
                            classes.choiceButton
                        )}
                        onClick={() => handleChoiceClick(choice)}
                        role="button"
                        style={{ backgroundColor: choice.color }}
                        tabIndex={0}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleChoiceClick(choice);
                            }
                        }}
                    >

                        {/* Choice Content */}
                        <div className={classes.choiceContent}>
                            <div style={{
                                fontSize: "14px",
                                fontFamily: `"${(theme as any).TextFont}", sans-serif`, // TODO: fix this
                                color: isSelected ? '#ffffff' : '#ffffff'
                            }}>
                                {choiceText}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

export default function ChoiceListTextHighlighterDropdownRenderer(props: Props) {
    const question = useQuestion();
    const { choices } = props;
    const theme = useTheme();
    const isMobile = React.useMemo(() => (typeof window !== "undefined" ? window.innerWidth <= MOBILE_WIDTH : false), []);
    const classes = useStyles({ theme, isMobile });
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [selectedChoice, setSelectedChoice] = useState<ChoiceModel | undefined>();
    const [dropdownPosition, setDropdownPosition] = useState<'top' | 'bottom'>('bottom');
    const dropdownRef = useRef<HTMLDivElement>(null);
    const selectCategoryText = useTranslatable(question, "SelectCategory");

    // Keep selectedChoice as undefined by default - no auto-selection
    // The dropdown should start with "Select category" placeholder

    // Early return AFTER all hooks have been called
    if (choices === undefined || choices.length === 0) {
        return null;
    }

    // Calculate dropdown position based on available space
    const calculateDropdownPosition = () => {
        if (dropdownRef.current) {
            const rect = dropdownRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;

            // If there's more space above and not enough below, show on top
            if (spaceAbove > spaceBelow && spaceBelow < 300) {
                setDropdownPosition('top');
            } else {
                setDropdownPosition('bottom');
            }
        }
    };

    const handleDropdownClick = () => {
        if (!isDropdownOpen) {
            calculateDropdownPosition();
        }
        setIsDropdownOpen(!isDropdownOpen);
    };

    const handleChoiceSelect = (choice: ChoiceModel) => {
        // Always update the local state first to change the dropdown color immediately
        setSelectedChoice(choice);

        // Close dropdown after selection
        setIsDropdownOpen(false);

        // Use the original onClick callback if provided
        if (props.onClick) {
            props.onClick(choice);
        }
    };

    const handleDropdownClose = () => {
        setIsDropdownOpen(false);
    };

    useEffect(() => {
        const targetCategoryName = props.selectedCategoryName;
        if (!targetCategoryName) {
            if (selectedChoice) {
                setSelectedChoice(undefined);
            }
            return;
        }

        const matchingChoice = choices.find((choice) => choice.name === targetCategoryName);
        if (matchingChoice && matchingChoice.name !== selectedChoice?.name) {
            setSelectedChoice(matchingChoice);
        }
    }, [props.selectedCategoryName, choices, selectedChoice]);

    // Get display text for the dropdown - call useTranslatable unconditionally
    const selectedChoiceText = useTranslatable(selectedChoice, "TEXT");
    const getDropdownText = () => {
        if (selectedChoice) {
            return removeHtmlTags(selectedChoiceText);
        }
        return selectCategoryText;
    };

    const role = question.type === QuestionType.RadioButton ? "radiogroup" : "group";


    return (
        <div role={role} className={classes.dropdownContainer} ref={dropdownRef}>
            <div
                className={classnames(
                    classes.dropdownSelect,
                    selectedChoice && classes.dropdownSelectHasValue,
                    props.hasError && classes.dropdownSelectError
                )}
                style={{
                    backgroundColor: selectedChoice?.color || undefined,
                    color: selectedChoice ? '#ffffff' : undefined
                }}
                onClick={handleDropdownClick}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleDropdownClick();
                    }
                }}
                aria-haspopup="listbox"
                aria-expanded={isDropdownOpen}
            >
                {props.hasError && (
                    <svg style={{ marginRight: "8px" }} xmlns="http://www.w3.org/2000/svg" width="15" height="16" viewBox="0 0 23 24" fill="none">
                        <path d="M10.9993 2.75C11.554 2.75 12.0658 3.04299 12.347 3.52349L20.7851 17.8995C21.0703 18.3839 21.0703 18.9816 20.7929 19.466C20.5156 19.9504 19.996 20.2513 19.4374 20.2513H2.56117C2.00254 20.2513 1.48297 19.9504 1.2056 19.466C0.928241 18.9816 0.932147 18.38 1.21342 17.8995L9.65152 3.52349C9.93279 3.04299 10.4445 2.75 10.9993 2.75ZM10.9993 7.75036C10.4797 7.75036 10.0617 8.16836 10.0617 8.68792V13.0632C10.0617 13.5828 10.4797 14.0008 10.9993 14.0008C11.5188 14.0008 11.9368 13.5828 11.9368 13.0632V8.68792C11.9368 8.16836 11.5188 7.75036 10.9993 7.75036ZM12.2494 16.501C12.2494 16.1694 12.1177 15.8515 11.8832 15.617C11.6488 15.3826 11.3308 15.2509 10.9993 15.2509C10.6677 15.2509 10.3498 15.3826 10.1153 15.617C9.88089 15.8515 9.74918 16.1694 9.74918 16.501C9.74918 16.8325 9.88089 17.1505 10.1153 17.3849C10.3498 17.6194 10.6677 17.7511 10.9993 17.7511C11.3308 17.7511 11.6488 17.6194 11.8832 17.3849C12.1177 17.1505 12.2494 16.8325 12.2494 16.501Z" fill="#F0345C" />
                    </svg>
                )}
                <span className={classes.dropdownText}>
                    {getDropdownText()}
                </span>
                <FontAwesomeIcon
                    icon={isDropdownOpen ? "caret-up" : "caret-down"}
                    className={classes.dropdownIcon}
                    style={{
                        color: props.hasError ? '#F0345C' : (selectedChoice ? '#ffffff' : undefined)
                    }}
                />
            </div>

            <ChoiceDropdown
                {...props}
                isOpen={isDropdownOpen}
                onClose={handleDropdownClose}
                onChoiceSelect={handleChoiceSelect}
                selectedChoice={selectedChoice}
                dropdownPosition={dropdownPosition}
                question={question}
            />

        </div>
    );
}
